import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Facebook, Github, Globe, Instagram, Linkedin, Mail, MessageCircle, Shield, Youtube } from 'lucide-react';

import { CALENDLY_URL, CONTACT_EMAIL } from '@/lib/site';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const whatsappUrl = 'https://wa.me/12026998621';
  
  const services = [
    { name: 'Creative Design', path: '/services?category=creatives' },
    { name: 'Software Development', path: '/services?category=software' },
    { name: 'SEO', path: '/services?category=seoai' },
    { name: 'AI Solutions', path: '/services?category=ai' },
    { name: 'AI Workflows', path: '/software/ai-workflow-automation' },
    { name: 'Google Ads Management', path: '/google-ads-management' },
  ];
  
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Products', path: '/products' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Contact', path: '/contact' },
    { name: 'Current Offers', path: '/google-ads-management' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Blog', path: '/blog' },
    { name: 'Industries', path: '/industries' },
    { name: 'Locations', path: '/locations' },
  ];
  
  const socialLinks = [
    { icon: <Linkedin size={20} />, path: 'https://www.linkedin.com/company/omnicraftservices/?viewAsMember=true', label: 'LinkedIn' },
    { icon: <Youtube size={20} />, path: 'https://www.youtube.com/@OmniCraftServices', label: 'YouTube' },
    { icon: <Instagram size={20} />, path: 'https://www.instagram.com/omnicraft_services', label: 'Instagram' },
    { icon: <Globe size={20} />, path: 'https://www.behance.net/OmniCraftServices', label: 'Behance' },
  ];

  return (
    <footer className="bg-slate-100 dark:bg-slate-900">
      <div className="container-custom pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-xl font-bold mb-4">Omnicraft Services</h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Comprehensive business solutions tailored to your needs. From creative design to software development, we've got you covered.
            </p>
            <p className="text-slate-600 dark:text-slate-400 mb-4">Based in Doha, Qatar</p>
            <div className="flex space-x-3 mt-4">
              {socialLinks.map((link, index) => (
                <a 
                  key={index} 
                  href={link.path} 
                  aria-label={link.label}
                  className="bg-slate-200 dark:bg-slate-800 p-2 rounded-full hover:bg-primary hover:text-white dark:hover:bg-primary transition-colors"
                >
                  {link.icon}
                </a>
              ))}
            </div>
          </div>
          
          {/* Services */}
          <div>
            <h3 className="text-xl font-bold mb-4">Our Services</h3>
            <ul className="space-y-2">
              {services.map((service, index) => (
                <li key={index}>
                  <Link 
                    to={service.path}
                    className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path}
                    className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-center">
                <Mail size={20} className="mr-2 text-primary" />
                <span className="text-slate-600 dark:text-slate-400">{CONTACT_EMAIL}</span>
              </li>
              <li className="flex items-center">
                <MessageCircle size={20} className="mr-2 text-primary" />
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-colors"
                >
                  WhatsApp
                </a>
              </li>
              <li className="flex items-center">
                <Calendar size={20} className="mr-2 text-primary" />
                <a 
                  href={CALENDLY_URL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-colors"
                >
                  Schedule a Call
                </a>
              </li>

            </ul>
          </div>
        </div>
        
        <hr className="border-slate-200 dark:border-slate-800 my-8" />
        
        {/* Bottom Footer */}
        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-slate-600 dark:text-slate-400 text-sm">
            &copy; {currentYear} Omnicraft Services. All rights reserved.
          </p>
          <div className="flex space-x-4 mt-4 md:mt-0">
            <Link to="/privacy-policy" className="text-sm text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary flex items-center gap-1">
              <Shield size={14} />
              <span>Privacy Policy</span>
            </Link>
            <Link to="/terms" className="text-sm text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
